import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { Card, CardActionArea, CardContent } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircleFlag } from "../../../common/CircleFlag";
import { useMatchCommentary } from "../../../common/contexts/aiCommentaryContext";
import { useTeamForm } from "../../../common/contexts/teamFormContext";
import { Bet } from "../../../types/Bet";
import { Match } from "../../../types/Match";
import { betOutcomeText } from "../../../utils/betScoring";
import { getMatchBettingState } from "../../../utils/betting";
import countryDict from "../../../utils/countryDict";
import { getDateFromDate, getLocalTimeFromDate } from "../../../utils/timeUtils";
import { matchVenueLabel } from "../../../utils/venue";
import BetOutcomeIcon from "../BetOutcomeIcon";
import CommentaryBadge from "../Commentary/CommentaryBadge";
import CommentaryModal from "../Commentary/CommentaryModal";
import "./FixtureTile.scss";
import TeamFormChips from "./TeamFormChips";

interface FixtureCardProps {
  match: Match;
  bet: Bet[];
  onClick: () => void;
}

const flagCode = (name?: string) => (name && countryDict.get(name)) || "pl";

const stageLabel = (match: Match) =>
  match.matchType.type === "Group"
    ? `Group ${match.matchType.value}`
    : match.matchType.value;

// Elegant, minimalistic mobile fixture card — shares the visual language of BetCard.
const FixtureCard: React.FC<FixtureCardProps> = ({ match, bet, onClick }) => {
  const navigate = useNavigate();
  const { getForm } = useTeamForm();
  const date = match.kickOffTime?.dateTime || "";
  const isPlayed = match.wasPlayed;
  const homeName = match.homeTeam.internationalName || "";
  const awayName = match.awayTeam.internationalName || "";
  const userBet = bet[0];
  const bettingState = getMatchBettingState(match);
  const bettingOpen = bettingState === "open";
  const matchAlreadyPlayed = bettingState === "played";

  const venue = matchVenueLabel(match);
  const homeForm = getForm(match.homeTeam.id);
  const awayForm = getForm(match.awayTeam.id);

  const { hasCommentary } = useMatchCommentary(match.id);
  const [commentaryOpen, setCommentaryOpen] = useState(false);

  const handleCommentaryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCommentaryOpen(true);
  };

  const handleBreakdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/match/${match.id}/bets-breakdown`);
  };

  return (
    <Card className="fixtureCard" onClick={onClick}>
      <CardActionArea component="div" disableRipple>
        <CardContent className="fixtureCard__content">
          <div className="fixtureCard__headerRow">
            <div className="fixtureCard__headerLeft">
              <span className="fixtureCard__stage">{stageLabel(match)}</span>
              {hasCommentary && (
                <CommentaryBadge onClick={handleCommentaryClick} />
              )}
              {matchAlreadyPlayed && (
                <button
                  type="button"
                  className="fixtureCard__contextBtn fixtureCard__contextBtn--corner"
                  onClick={handleBreakdownClick}
                  aria-label="View all league bets for this match"
                >
                  <FormatListBulletedOutlinedIcon />
                  Bets
                </button>
              )}
            </div>
            <span className="fixtureCard__datetime">
              <ScheduleRoundedIcon />
              {getDateFromDate(date)}
            </span>
          </div>

          {venue && (
            <div className="fixtureCard__venue">
              <PlaceOutlinedIcon />
              {venue}
            </div>
          )}

          <div className="fixtureCard__scoreRow">
            <div className="fixtureCard__team">
              <span className="fixtureCard__flag">
                <CircleFlag countryCode={flagCode(homeName)} height={48} width={48} />
              </span>
              <span className="fixtureCard__teamName">{homeName}</span>
              <TeamFormChips form={homeForm} align="center" />
            </div>

            <div className="fixtureCard__center">
              {isPlayed ? (
                <>
                  <span className="fixtureCard__scoreNum">
                    {match.homeScoreRegular}
                  </span>
                  <span className="fixtureCard__scoreSep">:</span>
                  <span className="fixtureCard__scoreNum">
                    {match.awayScoreRegular}
                  </span>
                </>
              ) : (
                <span className="fixtureCard__time">
                  {getLocalTimeFromDate(date)}
                </span>
              )}
            </div>

            <div className="fixtureCard__team">
              <span className="fixtureCard__flag">
                <CircleFlag countryCode={flagCode(awayName)} height={48} width={48} />
              </span>
              <span className="fixtureCard__teamName">{awayName}</span>
              <TeamFormChips form={awayForm} align="center" />
            </div>
          </div>

          {userBet ? (
            <div
              className={`fixtureCard__footer${
                matchAlreadyPlayed ? " fixtureCard__footer--withBreakdown" : ""
              }`}
            >
              {isPlayed ? (
                <span className="fixtureCard__betChip fixtureCard__betChip--result">
                  <BetOutcomeIcon
                    outcome={userBet.betResult}
                    styles={{ fontSize: 16, margin: 0 }}
                  />
                  {userBet.homeTeamScoreBet}–{userBet.awayTeamScoreBet} ·{" "}
                  {betOutcomeText(userBet.betResult)} · {userBet.points} pts
                </span>
              ) : (
                <span className="fixtureCard__betChip">
                  Your bet {userBet.homeTeamScoreBet}–{userBet.awayTeamScoreBet}
                </span>
              )}
            </div>
          ) : (
            <div
              className={`fixtureCard__footer${
                matchAlreadyPlayed ? " fixtureCard__footer--withBreakdown" : ""
              }`}
            >
              {bettingOpen ? (
                <span className="fixtureCard__betChip fixtureCard__betChip--empty">
                  No bet yet - tap to predict
                </span>
              ) : (
                <span className="fixtureCard__betChip fixtureCard__betChip--empty">
                  {matchAlreadyPlayed
                    ? "Match already played"
                    : "You did not place a bet before kickoff."}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </CardActionArea>

      <CommentaryModal
        open={commentaryOpen}
        onClose={() => setCommentaryOpen(false)}
        matchId={match.id}
        homeName={homeName}
        awayName={awayName}
      />
    </Card>
  );
};

export default FixtureCard;
